import { ISaSmsMigration } from '@interfaces/sa-sms';
import { Button, Form, notification, Radio, Space, Table, Tag, Typography } from 'antd';
import { useSaSmsWrite } from 'hooks/use-sa-sms-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaSmsReasonModal } from './sa-sms-reason-modal';
import { SmsMigrationAttachModal, SmsMigrationDocumentsModal } from './sms-migration-dialogs';

const COLOR: Record<string, string> = {
    submitted: 'processing',
    in_progress: 'processing',
    awaiting_customer: 'warning',
    completed: 'success',
    rejected: 'error',
    cancelled: 'default',
};
const OPEN = ['submitted', 'in_progress', 'awaiting_customer'];

type Props = {
    rows: ISaSmsMigration[];
    loading: boolean;
    canEdit: boolean;
    onOpenTenant: (id: string) => void;
    onDone: () => void;
};
type Update = { status: 'in_progress' | 'awaiting_customer' | 'rejected' };

/** Existing-number migration queue: status + notes (shown to the tenant), private documents, attach by Twilio number SID. */
export const SmsMigrationsTable: React.FC<Props> = ({
    rows,
    loading,
    canEdit,
    onOpenTenant,
    onDone,
}) => {
    const { t, i18n } = useTranslation(['sa-sms']);
    const { send } = useSaSmsWrite();
    const [updating, setUpdating] = useState<ISaSmsMigration | null>(null);
    const [attaching, setAttaching] = useState<ISaSmsMigration | null>(null);
    const [docsFor, setDocsFor] = useState<string | null>(null);

    const confirmUpdate = async ({ reason, status }: Update & { reason: string }) => {
        if (!updating) return;
        await send('put', `/migrations/${updating.id}`, { status, adminNotes: reason });
        notification.success({ message: t('saved') });
        setUpdating(null);
        onDone();
    };

    return (
        <>
            <Table<ISaSmsMigration>
                rowKey="id"
                loading={loading}
                dataSource={rows}
                pagination={false}
                scroll={{ x: 1100 }}
            >
                <Table.Column<ISaSmsMigration>
                    title={t('credits.date')}
                    render={(_, r) => new Date(r.createdAt).toLocaleString(i18n.language)}
                />
                <Table.Column<ISaSmsMigration>
                    title={t('list.tenant')}
                    render={(_, r) => (
                        <a onClick={() => onOpenTenant(r.tenantId)}>{r.tenantName ?? r.tenantId}</a>
                    )}
                />
                <Table.Column<ISaSmsMigration>
                    title={t('migrations.number')}
                    dataIndex="phoneE164"
                />
                <Table.Column<ISaSmsMigration>
                    title={t('migrations.provider')}
                    render={(_, r) =>
                        `${r.currentProvider}${
                            r.twilioAccountSid ? ` (${r.twilioAccountSid})` : ''
                        }`
                    }
                />
                <Table.Column<ISaSmsMigration>
                    title={t('migrations.holder')}
                    dataIndex="accountHolderName"
                />
                <Table.Column<ISaSmsMigration>
                    title={t('approvals.status')}
                    render={(_, r) => (
                        <Tag color={COLOR[r.status]}>{t(`migrations.status.${r.status}`)}</Tag>
                    )}
                />
                <Table.Column<ISaSmsMigration>
                    title={t('approvals.note')}
                    render={(_, r) => r.adminNotes ?? '-'}
                />
                <Table.Column<ISaSmsMigration>
                    title=""
                    render={(_, r) => (
                        <Space wrap>
                            {r.hasDocuments && (
                                <Button size="small" onClick={() => setDocsFor(r.id)}>
                                    {t('migrations.documents')}
                                </Button>
                            )}
                            {canEdit && OPEN.includes(r.status) && (
                                <>
                                    <Button size="small" onClick={() => setUpdating(r)}>
                                        {t('migrations.update')}
                                    </Button>
                                    <Button
                                        size="small"
                                        type="primary"
                                        onClick={() => setAttaching(r)}
                                    >
                                        {t('migrations.attach')}
                                    </Button>
                                </>
                            )}
                            {r.phoneNumberSid && (
                                <Typography.Text code>{r.phoneNumberSid}</Typography.Text>
                            )}
                        </Space>
                    )}
                />
            </Table>
            <SaSmsReasonModal<Update>
                open={!!updating}
                title={t('migrations.update')}
                initialValues={{ status: 'in_progress' }}
                onCancel={() => setUpdating(null)}
                onConfirm={confirmUpdate}
            >
                <Form.Item name="status" label={t('approvals.status')}>
                    <Radio.Group>
                        {(['in_progress', 'awaiting_customer', 'rejected'] as const).map((s) => (
                            <Radio key={s} value={s}>
                                {t(`migrations.status.${s}`)}
                            </Radio>
                        ))}
                    </Radio.Group>
                </Form.Item>
            </SaSmsReasonModal>
            <SmsMigrationAttachModal
                migration={attaching}
                onClose={() => setAttaching(null)}
                onDone={onDone}
            />
            <SmsMigrationDocumentsModal migrationId={docsFor} onClose={() => setDocsFor(null)} />
        </>
    );
};
